import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolTag } from '../../../models/core/sol-tag';
import { SolIconComponent } from '../sol-icon/sol-icon.component';
import { SolIcon } from '@sol/icons';
import { TranslatePipe } from '../../../pipes/translate.pipe';

@Component({
  selector: 'sol-tag',
  standalone: true,
  imports: [SolIconComponent, TranslatePipe],
  templateUrl: './sol-tag.component.html',
})
export class SolTagComponent extends AbstractComponent<SolTag> {
  protected readonly SolIcon = SolIcon;

  // Computed
  protected readonly hostClass$ = this.compute(tag => (tag.isIconOnly ? 'sol-tag sol-tag-icon-only' : 'sol-tag'));
  protected readonly styleVars$ = this.compute(tag => tag.styleVars);
  protected readonly icon$ = this.compute(tag => tag.icon);
  protected readonly text$ = this.compute(tag => tag.text);
  protected readonly info$ = this.compute(tag => tag.info);
  protected readonly hasText$ = this.compute(tag => tag.hasText);
  protected readonly hasInfo$ = this.compute(tag => tag.hasInfo);
  protected readonly childTags$ = this.compute(tag => tag.normalizedChildTags);
  protected readonly showChildren$ = this.compute(tag => tag.hasChildren);
  protected readonly showRemove$ = this.compute(tag => tag.removable && !tag.disabled);

  // Events
  @Output('remove')
  public removeEmitter = new EventEmitter<SolTag>();

  constructor() {
    super(SolTag.empty());
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  @HostBinding('style.--sol-tag-bg')
  public get hostBg(): string {
    return this.styleVars$()['--sol-tag-bg'];
  }

  @HostBinding('style.--sol-tag-border')
  public get hostBorder(): string {
    return this.styleVars$()['--sol-tag-border'];
  }

  @HostBinding('style.--sol-tag-text')
  public get hostText(): string {
    return this.styleVars$()['--sol-tag-text'];
  }

  @HostBinding('style.--sol-tag-icon')
  public get hostIcon(): string {
    return this.styleVars$()['--sol-tag-icon'];
  }

  @HostBinding('style.--sol-tag-text-weight')
  public get hostTextWeight(): string {
    return this.styleVars$()['--sol-tag-text-weight'];
  }

  @HostBinding('style.--sol-tag-height')
  public get hostHeight(): string {
    return this.styleVars$()['--sol-tag-height'];
  }

  @HostBinding('style.opacity')
  public get hostOpacity(): string {
    return this.styleVars$()['opacity'];
  }

  @Input()
  public set tag(tag: SolTag | undefined) {
    if (tag !== undefined) {
      this.patch(tag);
    }
  }

  protected onRemove(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const state = this.state;
    if (!state.removable || state.disabled) {
      return;
    }
    this.removeEmitter.emit(state);
  }

  protected onChildRemove(tag: SolTag): void {
    this.removeEmitter.emit(tag);
  }
}
